import { useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';

interface Article {
  title: string;
  summary: string;
  body: string[];
  action?: string;
}

interface Chapter {
  title: string;
  icon: string;
  color: string;
  articles: Article[];
}

const CHAPTERS: Chapter[] = [
  {
    title: 'Running Your Business Day-to-Day',
    icon: 'today',
    color: 'text-blue-600',
    articles: [
      {
        title: 'The Power of Systems and Routines',
        summary: 'Consistent daily habits are what separate thriving contractors from struggling ones.',
        body: [
          'The most successful contractors don\'t rely on memory — they rely on systems. A written morning routine, a weekly review, and an end-of-day wrap-up create consistency that clients notice and trust.',
          'Start each day: review the schedule, check for new messages, scan overdue invoices, confirm crew is en route. End each week: update job statuses, review what was billed vs. what was earned, plan next week.',
          'Use the My Tasks page to build your personal routine checklist. Check it every morning. Reset it every week. This single habit can eliminate 80% of dropped balls.',
        ],
        action: 'Go to My Tasks to set up your daily routine',
      },
      {
        title: 'Clear Communication is Your #1 Business Tool',
        summary: 'Most construction disputes come from miscommunication — not poor craftsmanship.',
        body: [
          'Set client expectations in writing from Day 1. Every quote should include scope, timeline, exclusions, and payment terms. A client who knows what to expect is a happy client — even when things go sideways.',
          'Communicate proactively. If you\'re running behind, call before they notice. If something unexpected comes up, document it as a change order and get approval before you proceed. Never do extra work for free without agreement.',
          'After every job, send a brief "job complete" message with a final photo or two. This closes the loop, prompts the payment conversation, and leaves a positive final impression.',
        ],
      },
      {
        title: 'Know Your Numbers',
        summary: 'A contractor who doesn\'t know their margins is working blind.',
        body: [
          'Your three most important numbers: (1) Your minimum hourly rate — the rate below which you lose money. (2) Your gross margin per job — revenue minus direct costs. (3) Your outstanding A/R — money earned but not yet collected.',
          'Review your dashboard every morning. If A/R is growing, you need to make collection calls. If margin is shrinking, your pricing or cost control has a leak.',
          'Price for profit, not just to win. Winning a job at a loss is worse than not winning it. Know your cost of labour, materials, equipment, and overhead before you quote.',
        ],
      },
    ],
  },
  {
    title: 'Client Relationships',
    icon: 'handshake',
    color: 'text-green-600',
    articles: [
      {
        title: 'How to Earn a Client for Life',
        summary: 'Repeat clients and referrals are the most profitable work you\'ll ever do.',
        body: [
          'The bar for "excellent" contractor experience is surprisingly low because so many contractors are unreliable. Show up when you say you will. Call when you say you will. Finish when you say you will. Do this consistently and you stand out immediately.',
          'During every job, update the client proactively — even a quick "We\'re on track, about 60% complete" message earns enormous goodwill. Silence breeds anxiety. Communication builds trust.',
          'After a job, follow up at 30 days. Ask how everything looks. This catches any small punch-list items before they become complaints. It also opens the door to the next project.',
          'Ask for referrals explicitly: "If you know anyone who needs similar work, I\'d really appreciate the introduction." People are happy to refer — they just need to be asked.',
        ],
      },
      {
        title: 'Handling Complaints and Disputes',
        summary: 'How you handle problems defines your reputation more than the problems themselves.',
        body: [
          'When a client complains, listen first. Don\'t get defensive. Let them finish. Then say: "I understand. Let me look into this and get back to you by [specific time]." Always follow through.',
          'Small fixes are almost always worth doing without argument, even if the client is being unreasonable. The cost of a 1-hour repair is far less than the cost of a bad Google review.',
          'For significant disputes, refer back to your signed quote or contract. Document everything. If it escalates, mediation through Consumer Protection BC is available before any legal action.',
          'Never do extra work without a signed change order. "I thought it was included" is the #1 source of payment disputes. When in doubt, write it down.',
        ],
      },
      {
        title: 'Building Your Portfolio and Reputation',
        summary: 'Your reputation is your most valuable business asset.',
        body: [
          'Document every job with before and after photos. Ask permission to use them. A well-curated photo portfolio is more convincing than any advertising.',
          'Request Google reviews from happy clients immediately after job completion. Timing matters — ask within a week of finishing while the positive experience is fresh.',
          'Consider a simple follow-up process: 1-week check-in call, 30-day touch base, Christmas card or year-end note. The contractors who stay top-of-mind get the first call.',
        ],
      },
    ],
  },
  {
    title: 'Financial Management',
    icon: 'account_balance',
    color: 'text-amber-600',
    articles: [
      {
        title: 'Cash Flow is King',
        summary: 'Many profitable businesses fail because they run out of cash. Don\'t let this be you.',
        body: [
          'Cash flow is the timing of money in vs. money out. You can be profitable on paper but still miss payroll if clients pay slowly. Invoice immediately upon milestone completion — never wait until the end of a job.',
          'Use deposit invoices for every job over $5,000. A 30–50% deposit protects you from "phantom" clients and ensures you\'re not financing their renovation. It\'s standard practice and professional clients expect it.',
          'Track your A/R aging weekly. Invoices over 30 days need a phone call. Over 60 days need a formal demand. Over 90 days, consider involving collections. The older a debt gets, the harder it is to collect.',
          'Keep 2 months of operating expenses in a reserve account. This is your "sleep at night" fund. Build it slowly — even $500/month adds up.',
        ],
      },
      {
        title: 'Pricing for Profit, Not Just to Win',
        summary: 'The right price reflects your true cost plus a margin that lets you grow.',
        body: [
          'Your cost has four components: (1) Labour — your time and crew time. (2) Materials — what you pay, not what you charge. (3) Overhead — truck, insurance, tools, software, phone, accounting. (4) Profit — your return for running the business.',
          'A common mistake: quoting labour at cost with no overhead. If your hourly wage is $35/hr but you have $25/hr in overhead per billable hour, your break-even is $60/hr — not $35.',
          'For BC residential work in carpentry/general contracting, the market rate is typically $75–$125/hr for a skilled tradesperson with overhead factored in. Be cautious of clients who push for $50/hr — they often become problem clients.',
          'Review your project margins after every job. If you\'re consistently under 20% gross margin on labour, your pricing needs adjustment.',
        ],
      },
      {
        title: 'GST, Taxes, and Your Obligations',
        summary: 'Stay compliant. Tax surprises can destroy a business.',
        body: [
          'If your revenues exceed $30,000 in a 12-month period, you must register for GST. Once registered, you must collect 5% GST on taxable services and remit quarterly or annually.',
          'Set aside 25–30% of every payment received for income tax. Pay your income tax instalments on time to avoid CRA interest. Better yet, work with an accountant quarterly, not just at year end.',
          'Keep every business receipt — digital is fine. Fuel, tools, materials, software subscriptions, safety gear, and professional development are all deductible business expenses.',
          'Pay yourself a salary or regular draw, not just "whatever\'s left." This forces discipline and makes your financial picture clearer for tax planning.',
        ],
      },
      {
        title: 'Insurance You Actually Need',
        summary: 'One uninsured incident can end your business.',
        body: [
          'Commercial General Liability (CGL): $2M minimum, $5M recommended. Required by most general contractors and property owners. Protects you if your work causes property damage or injury.',
          'WCB (WorkSafeBC): If you have employees (or subcontractors in some cases), WCB coverage is mandatory. Even sole proprietors benefit from personal coverage. Register at worksafebc.com.',
          'Commercial vehicle insurance: Your personal auto policy likely excludes commercial use. If you use your truck for work, you need commercial coverage.',
          'Tools and equipment insurance: Covers theft and damage of tools on site and in your truck. Often inexpensive and essential.',
        ],
      },
    ],
  },
  {
    title: 'Team & Leadership',
    icon: 'groups',
    color: 'text-purple-600',
    articles: [
      {
        title: 'Hiring Well and Keeping Good People',
        summary: 'Your crew is your product. Hire slowly, develop deliberately.',
        body: [
          'The best workers come from referrals from your existing crew. Ask your best employees who else they\'d want to work with. This is more reliable than job postings.',
          'During the first 90 days, check in weekly with new hires. Set clear expectations, give feedback often, and give them a chance to succeed. Most turnover happens in the first 3 months.',
          'Pay fair wages. The "savings" of underpaying are erased by turnover costs, poor quality, and your own time spent re-hiring. In Duncan/North Cowichan, know the market rates and stay competitive.',
          'Recognize good work publicly and often. A short "great work on that job, I appreciate the effort" costs nothing and builds loyalty that money can\'t buy.',
        ],
      },
      {
        title: 'Field Leadership — Running a Tight Ship',
        summary: 'What happens on site every day determines your profitability.',
        body: [
          'The job starts with a clear scope briefing. Every crew member should know: what we\'re doing today, what "done" looks like, any safety hazards to watch for, and who\'s the decision-maker on site.',
          'Waste is profit lost. Watch for: crew waiting for materials, doing work twice, long lunch breaks, and time not logged accurately. Each is a leak in your bucket.',
          'Site cleanliness reflects professionalism. A tidy site impresses homeowners, reduces liability, and is genuinely faster to work on. Build it into the daily routine.',
          'When something goes wrong on site, own it fast. A foreman who says "we have a problem, here\'s our solution" builds trust. One who covers it up destroys it.',
        ],
      },
      {
        title: 'Safety Culture',
        summary: 'Safety isn\'t a cost — it\'s a competitive advantage.',
        body: [
          'In BC, WorkSafeBC regulations are law. A safety violation can result in fines, stop-work orders, or personal liability. More importantly, someone could get hurt.',
          'Develop a simple safety checklist for your most common job types. Use it. Make it easy for crew to flag hazards without fear of embarrassment.',
          'Incidents cost money in lost time, workers\' comp premiums, investigations, and legal exposure. A strong safety record keeps premiums down and allows you to bid more work.',
          'Lead by example. If you skip a step on the safety checklist, your crew will too. If you always wear PPE, they\'ll follow. Culture flows from the top.',
        ],
      },
    ],
  },
  {
    title: 'Growth & Strategy',
    icon: 'rocket_launch',
    color: 'text-rose-600',
    articles: [
      {
        title: 'Growing Without Losing Control',
        summary: 'The biggest mistake in contracting is growing faster than your systems can handle.',
        body: [
          'Before you hire your next employee, document what you need them to do with enough detail that a new person could follow it. If you can\'t write it down, you\'re not ready to delegate it.',
          'Revenue growth without margin growth is just more chaos. Before adding volume, ask: "Can I do this more profitably?" Tighten existing operations before expanding.',
          'The goal of systems is to make yourself less essential to day-to-day operations — not less valuable, but less bottlenecked. Every time you do a task, ask: "Could I build a process so someone else does this?"',
        ],
      },
      {
        title: 'Choosing the Right Work',
        summary: 'Not all clients and jobs are worth taking. Say no to protect your business.',
        body: [
          'The 20/80 rule in contracting: 20% of your clients will give you 80% of your headaches. Learn to spot them early: price shoppers, scope-creepers, those who don\'t respect your time, and those with unrealistic expectations.',
          'Every new job should meet at least 2 of 3 criteria: good margin, good client, or interesting work. A job that fails all three will drain your energy and resources.',
          'Your best job type is one where you have experience, can price confidently, and have a good reputation to leverage. Double down on your strengths before diversifying.',
        ],
      },
      {
        title: 'Your Professional Brand',
        summary: 'OchrePoint Contracting is a brand. Every interaction shapes it.',
        body: [
          '"Built on Skill, Finished with Pride" isn\'t just a tagline — it\'s a standard. Every job should end with you confident that you\'d show a photo to your best client.',
          'Your truck, uniforms, signage, and communications are marketing. A clean, marked vehicle on a job site generates inquiries. Professional looking quotes win jobs before the price is even discussed.',
          'Your online presence matters. A complete Google Business profile with photos and reviews will drive 30–50% of local leads. Invest 30 minutes updating it — it\'s the highest-ROI marketing you can do.',
        ],
      },
    ],
  },
];

export const BusinessGuidePage = () => {
  const [openChapter, setOpenChapter] = useState<number | null>(0);
  const [openArticle, setOpenArticle] = useState<string | null>(null);

  const toggleChapter = (i: number) => setOpenChapter(openChapter === i ? null : i);
  const toggleArticle = (key: string) => setOpenArticle(openArticle === key ? null : key);

  return (
    <MainLayout>
      <div className="flex flex-col min-h-screen">
        <header className="sticky top-14 md:top-0 z-20 border-b border-gray-200 px-4 py-4 md:px-8 md:py-5 bg-gray-50/80 backdrop-blur-sm">
          <div className="flex items-center gap-4">
            <div className="bg-primary/10 rounded-md p-2">
              <span className="material-symbols-outlined text-primary text-2xl">school</span>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Business Excellence Guide</h2>
              <p className="text-sm text-gray-500">Running OchrePoint Contracting to the highest standards</p>
            </div>
          </div>
        </header>

        <div className="p-4 md:p-8">
          <div className="max-w-3xl mx-auto">
            <div className="mb-6 p-5 bg-primary/5 border border-primary/20 rounded-2xl">
              <p className="text-sm text-gray-700 leading-relaxed">
                This guide compiles hard-won wisdom from successful contractors and business principles
                tailored to the realities of running a small construction company in BC.
                Read it as a reference, not a manual — return to chapters when a specific challenge arises.
              </p>
            </div>

            <div className="space-y-3">
              {CHAPTERS.map((chapter, ci) => {
                const isOpen = openChapter === ci;
                return (
                  <div key={ci} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => toggleChapter(ci)}
                      className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`material-symbols-outlined ${chapter.color}`}>{chapter.icon}</span>
                        <div className="text-left">
                          <p className="font-semibold text-gray-900">{chapter.title}</p>
                          <p className="text-xs text-gray-400">{chapter.articles.length} articles</p>
                        </div>
                      </div>
                      <span className={`material-symbols-outlined text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}>expand_more</span>
                    </button>

                    {isOpen && (
                      <div className="border-t border-gray-100 divide-y divide-gray-50">
                        {chapter.articles.map((article, ai) => {
                          const key = `${ci}-${ai}`;
                          const isArticleOpen = openArticle === key;
                          return (
                            <div key={ai}>
                              <button
                                onClick={() => toggleArticle(key)}
                                className="w-full flex items-start justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
                              >
                                <div className="pr-4">
                                  <p className="text-sm font-semibold text-gray-900">{article.title}</p>
                                  <p className="text-xs text-gray-500 mt-0.5">{article.summary}</p>
                                </div>
                                <span className={`material-symbols-outlined text-gray-400 flex-shrink-0 text-base mt-1 transition-transform ${isArticleOpen ? 'rotate-180' : ''}`}>expand_more</span>
                              </button>

                              {isArticleOpen && (
                                <div className="px-5 pb-5">
                                  <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                                    {article.body.map((para, pi) => (
                                      <p key={pi} className="text-sm text-gray-700 leading-relaxed">{para}</p>
                                    ))}
                                  </div>
                                  {article.action && (
                                    <div className="mt-3 flex items-center gap-2 text-xs text-primary">
                                      <span className="material-symbols-outlined text-sm">arrow_forward</span>
                                      <span className="font-medium">{article.action}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-8 p-5 bg-gray-50 border border-gray-200 rounded-xl text-center">
              <p className="text-sm text-gray-600 font-medium mb-1">"Built on Skill, Finished with Pride"</p>
              <p className="text-xs text-gray-400">OchrePoint Contracting LTD — Duncan & North Cowichan, BC</p>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};
